import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useTranslation } from 'react-i18next';
import { HSE_TERMS, type HseTermKey } from '../../constants/hseTerms';
import { ACCEPTED_FILE_TYPES } from '../../services/utility-proof.service';
import type { HseEvidenceFile } from '../../types/hse-weekly-report.type';
import { formatDateTime } from '../../utils/hseFormat';

const WHOLE_WEEK = '__week__';

export interface HseEvidencePanelProps {
  files: HseEvidenceFile[];
  readOnly: boolean;
  disabled?: boolean;
  uploading: boolean;
  onUpload: (files: File[], termKey: HseTermKey | null) => void;
  onRemove: (evidenceId: string) => void;
}

/** Supporting documents for the week: incident reports, photos, medical notes. */
const HseEvidencePanel: React.FC<HseEvidencePanelProps> = ({ files, readOnly, disabled, uploading, onUpload, onRemove }) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [target, setTarget] = useState<string>(WHOLE_WEEK);

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    onUpload(Array.from(list), target === WHOLE_WEEK ? null : (target as HseTermKey));
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Paper
      elevation={0}
      sx={{ mt: 3, p: { xs: 2, md: 2.5 }, borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: files.length ? 1.5 : 0 }}>
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileOutlinedIcon fontSize="small" color="primary" />
            {t('hse.evidence.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('hse.evidence.subtitle')}
          </Typography>
        </Box>
        {!readOnly && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <TextField
              select
              size="small"
              label={t('hse.evidence.attachTo')}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={disabled || uploading}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value={WHOLE_WEEK}>{t('hse.evidence.wholeWeek')}</MenuItem>
              {HSE_TERMS.map((term) => (
                <MenuItem key={term.key} value={term.key}>
                  {t(`hse.terms.${term.key}.label`)}
                </MenuItem>
              ))}
            </TextField>
            <input
              ref={inputRef}
              type="file"
              hidden
              multiple
              accept={ACCEPTED_FILE_TYPES}
              onChange={(e) => handleFiles(e.target.files)}
            />
            <Button
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileOutlinedIcon />}
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
            >
              {t('hse.evidence.upload')}
            </Button>
          </Box>
        )}
      </Box>

      {files.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {readOnly ? t('hse.evidence.noneSubmitted') : t('hse.evidence.empty')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {files.map((file) => (
            <ListItem
              key={file.id}
              divider
              secondaryAction={
                !readOnly && (
                  <Tooltip title={t('hse.evidence.remove')}>
                    <span>
                      <IconButton edge="end" aria-label={t('hse.evidence.remove')} onClick={() => onRemove(file.id)} disabled={disabled}>
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AttachFileOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  file.download_url ? (
                    <Link href={file.download_url} target="_blank" rel="noopener noreferrer" underline="hover">
                      {file.original_name ?? file.asset_id}
                    </Link>
                  ) : (
                    file.original_name ?? file.asset_id
                  )
                }
                secondary={formatDateTime(file.created_at, i18n.language)}
              />
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                label={file.term_key ? t(`hse.terms.${file.term_key}.label`) : t('hse.evidence.wholeWeek')}
                sx={{ marginInlineEnd: readOnly ? 0 : 5, maxWidth: 260 }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default HseEvidencePanel;
